/**
 * Product Siblings Loader (Simplified)
 *
 * Uses embedded JSON data from metaobject
 * All sibling data is pre-loaded via Liquid at page render time.
 */

(function() {
  'use strict';

  class ProductSiblingsLoader {
    constructor() {
      this.container = null;
      this.data = null;
      this.currentProduct = null;
      this.isUpdating = false;
      this.selectedColour = null;
      this.selectedHardware = null;
      this.isPromoProduct = false;
      this.sectionId = null;

      this.excludedVersions = ['Preloved'];
      this.currentVersion = null;

      // Cached DOM references for performance
      this.elements = {
        swatchGrid: null,
        hardwareContainer: null,
        descriptionText: null
      };

      // Hardware lookup map for O(1) availability checks
      this.hardwareLookup = new Map();

      // Deferred modal update - stores pending product until modal opened
      this.pendingModalProduct = null;
    }

  /**
   * Initialize from embedded JSON data
   */
  init() {
    this.container = document.querySelector('.parent-products-wrapper');
    if (!this.container) {
      return;
    }

    // Cache DOM references once
    this.elements.swatchGrid = this.container.querySelector('.colour-swatches-grid');
    this.elements.hardwareContainer = this.container.querySelector('.hardware-filter-container');
    this.elements.descriptionText = this.container.querySelector('.current-product-text');

    // Cache section ID for gallery/modal queries
    this.sectionId = document.querySelector('[data-section]')?.dataset.section;

    const dataScript = document.getElementById('product-siblings-data');
    if (!dataScript) {
      this.container.setAttribute('data-filter-state', 'hidden');
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);
      this.currentProduct = this.data.currentProduct;
      this.isPromoProduct = this.container.dataset.isPromo === 'true';

      const current = this.data.siblings.find(s => s.handle === this.currentProduct.handle);
      if (current) {
        this.selectedColour = current.colour.label;
        this.selectedHardware = current.hardware;
        this.currentVersion = current.version;
      }

      // Render immediately from Liquid data (the current product is always included)
      this.render();
      this.initBrowserHistory();
      this.initModalListener();

      // If Liquid truncated siblings at 50, fetch the rest in the background and re-render
      if (this.data.siblingsTotal > this.data.siblings.length) {
        this.fetchMissingSiblings().then(() => this.render());
      }

    } catch (e) {
      this.container.setAttribute('data-filter-state', 'hidden');
    }
  }

  /**
   * Fetch siblings missing due to Liquid's 50-item metaobject list cap.
   * Uses /products.json filtered by product_type, then matches client-side
   * by title prefix against parentProduct. Derives version/hardware from tags.
   */
  async fetchMissingSiblings() {
    const parentProduct = this.data.parentProduct;
    const productType = this.data.productType;
    if (!parentProduct || !productType) return;

    // Build colour lookup - first seed from Liquid-rendered siblings and colourMap
    const colourLookup = new Map();
    if (this.data.colourMap) {
      Object.entries(this.data.colourMap).forEach(([label, data]) => {
        colourLookup.set(label, { hex: data.hex, image: data.image });
      });
    }
    this.data.siblings.forEach(s => {
      if (s.colour && s.colour.label && !colourLookup.has(s.colour.label)) {
        colourLookup.set(s.colour.label, { hex: s.colour.hex, image: s.colour.image });
      }
    });
    const colourPromise = (async () => {
    // Fetch full colour map via Section Rendering API to get all colours beyond Liquid cap
    try {
      const colourRes = await fetch('/?section_id=colour-map-data');
      if (colourRes.ok) {
        const colourText = await colourRes.text();
        // Section Rendering API wraps output in <div ...>...</div> — parse with DOM
        const doc = new DOMParser().parseFromString(colourText, 'text/html');
        const inner = doc.querySelector('.shopify-section').textContent.trim();
        const colourJson = JSON.parse(`{${inner}}`);
        Object.entries(colourJson).forEach(([label, data]) => {
          colourLookup.set(label, { hex: data.hex, image: data.image });
        });
      }
    } catch (e) {
      console.warn('[ProductSiblings] colour map section fetch failed:', e);
    }
    })();

    const knownHandles = new Set(this.data.siblings.map(s => s.handle));
    let page = 1;
    const fetched = [];

    try {
      // Pages are requested a few ahead of the one being processed, so they download in parallel
      const pageRequests = new Map();
      const getPage = (n) => {
        if (!pageRequests.has(n)) {
          pageRequests.set(n, fetch(`/products.json?product_type=${encodeURIComponent(productType)}&limit=250&page=${n}`)
            .then(r => (r.ok ? r.json() : null))
            .catch(() => null));
        }
        return pageRequests.get(n);
      };
      for (let n = 1; n <= 4; n++) getPage(n);

      while (true) {
        getPage(page + 4);
        const json = await getPage(page);
        if (!json) break;
        const products = json.products || [];
        if (products.length === 0) break;

        for (const p of products) {
          if (knownHandles.has(p.handle)) continue;

          // Only include products belonging to this sibling group
          const titlePrefix = p.title.split(' | ')[0];
          if (titlePrefix !== parentProduct) continue;

          const parts = p.title.split(' | ');
          const colourAndHardware = parts[1] || '';
          const tags = Array.isArray(p.tags) ? p.tags : (p.tags ? p.tags.split(',').map(t => t.trim()) : []);
          const hardwareTag = tags.find(t => t.startsWith('hardware_'));
          // Tag is not reliably present — fall back to the title ("... with Gold hardware")
          const titleHardware = (colourAndHardware.match(/ with (Gold|Silver) hardware$/i) || [])[1];
          const hardware = hardwareTag
            ? hardwareTag.replace('hardware_', '')
            : (titleHardware ? titleHardware.charAt(0).toUpperCase() + titleHardware.slice(1).toLowerCase() : '');
          const version = tags.includes('version-signature') ? 'Signature' : 'Promo';

          const colourLabel = colourAndHardware
            .replace(/ with (Gold|Silver) hardware$/i, '')
            .trim() || titlePrefix;

          // Look up colour from known siblings (Liquid-rendered) or previously fetched
          const knownColour = colourLookup.get(colourLabel);

          const variant = p.variants[0];
          // inventory_quantity is -1 for "continue selling" (treat as in stock)
          // inventory_quantity may be undefined in some API contexts — fall back to available flag
          const inventoryQty = variant.inventory_quantity === undefined
            ? (p.variants.some(v => v.available) ? 999 : 0)
            : variant.inventory_quantity == null ? 0
            : variant.inventory_quantity < 0 ? 999
            : variant.inventory_quantity;

          const sibling = {
            id: p.id,
            handle: p.handle,
            title: p.title,
            url: `/products/${p.handle}`,
            available: p.variants.some(v => v.available),
            variantId: variant.id,
            price: Math.round(parseFloat(variant.price) * 100),
            compareAtPrice: variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : 0,
            version: version,
            hardware: hardware,
            colour: {
              label: colourLabel,
              hex: knownColour ? knownColour.hex : '#cccccc',
              image: knownColour ? knownColour.image : ''
            },
            featuredImage: p.images[0]?.src || '',
            images: (p.images || []).slice(0, 10).map(img => ({
              id: img.id,
              url: img.src,
              thumbnailUrl: img.src,
              alt: img.alt || p.title,
              width: img.width || 1200,
              height: img.height || 1200
            })),
            tags: tags.filter(t => t.startsWith('badge_')),
            description: p.body_html || '',
            metaobjectDescription: '',
            useProductDescription: false,
            quantityAvailable: inventoryQty
          };

          fetched.push(sibling);
        }

        if (products.length < 250) break;
        page++;
      }
    } catch (e) {
      console.warn('[ProductSiblings] fetchMissingSiblings failed:', e);
    }

    // Colour map ran in parallel with the product pages — apply it to the fetched products now
    await colourPromise;
    fetched.forEach(sib => {
      const c = colourLookup.get(sib.colour.label);
      if (c) {
        sib.colour.hex = c.hex;
        sib.colour.image = c.image;
      }
    });

    this.data.siblings = this.data.siblings.concat(fetched);
  }

  /**
   * Initialize modal open listener to update modal content on demand
   * Listens on opener buttons so modal updates BEFORE opening
   */
  initModalListener() {
    if (!this.sectionId) return;
    
    const gallery = document.querySelector(`#Slider-Gallery-${this.sectionId}`);
    if (!gallery) return;

    // Listen on modal opener buttons - updates modal BEFORE it opens
    gallery.addEventListener('click', (e) => {
      const opener = e.target.closest('.product__modal-opener');
      if (!opener) return;
      
      if (this.pendingModalProduct) {
        this.updateProductModal(this.pendingModalProduct);
        this.pendingModalProduct = null;
      }
    }, { capture: true });
  }

  /**
   * Filter siblings based on promo/full-price status and excluded versions
   */
  getFilteredSiblings() {
    return this.data.siblings.filter(product => {
      if (this.excludedVersions.includes(product.version)) {
        return false;
      }

      if (this.currentVersion === 'Complimentary') {
        return product.version === 'Complimentary';
      }

      const isFullPrice = product.version === 'Signature';

      if (this.isPromoProduct) {
        return !isFullPrice;
      } else {
        return isFullPrice;
      }
    });
  }

  /**
   * Group siblings by colour label
   */
  groupByColour(siblings) {
    const groups = {};

    siblings.forEach(product => {
      const colourKey = product.colour.label;
      if (!groups[colourKey]) {
        groups[colourKey] = [];
      }
      groups[colourKey].push(product);
    });

    return groups;
  }

  /**
   * Get available hardware options from siblings
   */
  getHardwareOptions(siblings) {
    const hardware = new Set();
    siblings.forEach(p => {
      if (p.hardware) hardware.add(p.hardware);
    });
    return Array.from(hardware);
  }

  /**
   * Render all UI components
   */
  render() {
    const filtered = this.getFilteredSiblings();

    if (filtered.length === 0) {
      this.container.setAttribute('data-filter-state', 'hidden');
      return;
    }

    const colourGroups = this.groupByColour(filtered);
    const hardwareOptions = this.getHardwareOptions(filtered);

    this.filteredSiblings = filtered;
    this.colourGroups = colourGroups;
    this.hardwareOptions = hardwareOptions;

    // Build hardware lookup map for O(1) availability checks
    this.hardwareLookup.clear();
    filtered.forEach(p => {
      const key = `${p.colour.label}|${p.hardware}`;
      this.hardwareLookup.set(key, p);
    });

    const swatchesHTML = this.buildColourSwatchesHTML(colourGroups);
    const hardwareHTML = hardwareOptions.length > 1
      ? this.buildHardwareHTML(filtered, hardwareOptions)
      : '';

    this.elements.swatchGrid.innerHTML = swatchesHTML;
    this.elements.hardwareContainer.innerHTML = hardwareHTML;

    this.initSwatchListeners();
    this.initHardwareListeners();

    this.container.setAttribute('data-filter-state', 'ready');
  }

  /**
   * Build colour swatches HTML
   */
  buildColourSwatchesHTML(colourGroups) {
    let html = '';
    let swatchIndex = 0;

    Object.entries(colourGroups).forEach(([colourLabel, products]) => {
      let targetProduct = products.find(p => p.hardware === this.selectedHardware);
      if (!targetProduct) targetProduct = products[0];

      const hasCurrentHardware = this.selectedHardware
        ? products.some(p => p.hardware === this.selectedHardware)
        : true;

      const isActive = colourLabel === this.selectedColour;
      const wrapperClass = `colour-swatch-wrapper${!hasCurrentHardware ? ' not-available' : ''}`;
      const swatchClass = `colour-swatch${isActive ? ' active' : ''}${!hasCurrentHardware ? ' not-available' : ''}`;

      // Lazy load swatches beyond the first 6 for performance
      const loadingAttr = swatchIndex < 6 ? 'eager' : 'lazy';
      const priorityAttr = swatchIndex < 6 ? 'high' : 'auto';

      let swatchContent = '';
      if (targetProduct.colour.image) {
        swatchContent = `<img class="colour-swatch__image" src="${targetProduct.colour.image}" alt="${colourLabel}" width="28" height="28" loading="${loadingAttr}" fetchpriority="${priorityAttr}" decoding="async">`;
      } else {
        swatchContent = `<span class="colour-swatch__color" style="background-color: ${targetProduct.colour.hex};"></span>`;
      }

      html += `
        <div class="${wrapperClass}"
             data-colour-label="${colourLabel}"
             data-product-handle="${targetProduct.handle}">
          <button type="button" class="${swatchClass}"
                  aria-label="${colourLabel}"
                  data-swatch-click="true">
            ${swatchContent}
          </button>
        </div>
      `;

      swatchIndex++;
    });

    return html;
  }

  /**
   * Build hardware filter HTML
   */
  buildHardwareHTML(siblings, hardwareOptions) {
    const orderedHardware = ['Gold', 'Silver'].filter(hw => hardwareOptions.includes(hw));

    const currentSibling = siblings.find(s => s.handle === this.currentProduct.handle);

    const buttons = orderedHardware.map(hw => {
      const isActive = hw === this.selectedHardware;
      const exists = siblings.some(s =>
        s.colour.label === this.selectedColour && s.hardware === hw
      );

      let buttonClass = 'hardware-filter-button';
      if (isActive) buttonClass += ' active';
      else if (exists) buttonClass += ' inactive';
      else buttonClass += ' not-available';

      return `
        <button class="${buttonClass}" data-hardware="${hw}">
          ${hw.toUpperCase()}
        </button>
      `;
    }).join('');

    let inventoryHTML = '';
    if (currentSibling) {
      const qty = currentSibling.quantityAvailable;
      const iconSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" aria-hidden="true" focusable="false" class="icon icon-inventory-status"><circle cx="7.5" cy="7.5" r="6" fill="none" stroke="currentColor" stroke-miterlimit="10" opacity="0.3"/><circle cx="7.5" cy="7.5" r="3" fill="currentColor"/></svg>';

      let color, text;
      if (qty > 5) {
        color = 'rgb(62, 214, 96)';
        text = 'In stock';
      } else if (qty > 0) {
        color = 'rgb(238, 148, 65)';
        text = `Low stock: ${qty} left`;
      } else {
        color = 'rgb(200, 200, 200)';
        text = 'Out of stock';
      }

      inventoryHTML = `
        <div class="product__inventory product__inventory--hardware" role="status">
          <span class="svg-wrapper" style="color: ${color}">${iconSVG}</span>
          ${text}
        </div>
      `;
    }

    return `
      <div class="filter-section hardware-filter-section">
        <div class="filter-label">HARDWARE</div>
        <div class="filter-content">
          <div class="filter-options">${buttons}</div>
          ${inventoryHTML}
        </div>
      </div>
    `;
  }

  /**
   * Initialize swatch click and hover listeners (event delegation)
   */
  initSwatchListeners() {
    // Delegated on a stable container, so bind once even if render() runs again
    if (this.swatchListenersBound) return;
    this.swatchListenersBound = true;

    // Delegated click handler on swatch grid
    this.elements.swatchGrid.addEventListener('click', async (e) => {
      const button = e.target.closest('[data-swatch-click="true"]');
      if (!button || this.isUpdating) return;

      e.preventDefault();
      const wrapper = button.closest('.colour-swatch-wrapper');
      const colourLabel = wrapper.dataset.colourLabel;

      this.selectedColour = colourLabel;

      this.elements.swatchGrid.querySelectorAll('.colour-swatch').forEach(s => s.classList.remove('active'));
      button.classList.add('active');

      const matchingProduct = this.filteredSiblings.find(p =>
        p.colour.label === colourLabel &&
        (this.selectedHardware ? p.hardware === this.selectedHardware : true)
      );

      if (matchingProduct) {
        this.isUpdating = true;
        try {
          await this.updateProduct(matchingProduct);
        } finally {
          this.isUpdating = false;
        }
      } else {
        this.updateButtonState(null);
      }
    });

    // Delegated hover handlers using capture phase for mouseenter/mouseleave
    this.elements.swatchGrid.addEventListener('mouseenter', (e) => {
      const button = e.target.closest('[data-swatch-click="true"]');
      if (!button || !this.elements.descriptionText) return;
      const wrapper = button.closest('.colour-swatch-wrapper');
      this.elements.descriptionText.textContent = wrapper.dataset.colourLabel;
    }, true);

    this.elements.swatchGrid.addEventListener('mouseleave', (e) => {
      const button = e.target.closest('[data-swatch-click="true"]');
      if (!button || !this.elements.descriptionText) return;
      this.elements.descriptionText.textContent = this.selectedColour;
    }, true);
  }

  /**
   * Initialize hardware button listeners (event delegation)
   */
  initHardwareListeners() {
    if (this.hardwareListenersBound) return;
    this.hardwareListenersBound = true;

    // Delegated click handler on hardware container
    this.elements.hardwareContainer.addEventListener('click', async (e) => {
      const button = e.target.closest('.hardware-filter-button');
      if (!button || this.isUpdating) return;

      e.preventDefault();
      const hardware = button.dataset.hardware;
      this.selectedHardware = hardware;

      this.elements.hardwareContainer.querySelectorAll('.hardware-filter-button').forEach(b => {
        b.classList.remove('active');
        b.classList.add('inactive');
      });
      button.classList.remove('inactive');
      button.classList.add('active');

      const matchingProduct = this.filteredSiblings.find(p =>
        p.colour.label === this.selectedColour && p.hardware === hardware
      );

      if (matchingProduct) {
        this.isUpdating = true;
        try {
          await this.updateProduct(matchingProduct);
          this.updateColourSwatchHandles();
        } finally {
          this.isUpdating = false;
        }
      } else {
        this.updateButtonState(null);
        this.updateColourSwatchHandles();
      }
    });
  }

  /**
   * Preload all product images before updating DOM
   * Returns a promise that resolves when all images are loaded (or timeout)
   * Uses shorter timeout on mobile (only 1 visible image) vs desktop (multiple images)
   */
  preloadImages(product, timeoutMs) {
    if (!product.images || product.images.length === 0) {
      return Promise.resolve();
    }

    const imagePromises = product.images.map(img => {
      return new Promise(resolve => {
        const preloadImg = new Image();
        preloadImg.onload = resolve;
        preloadImg.onerror = resolve; // Don't block on failed loads
        preloadImg.src = img.url;
        
        // Also preload thumbnail if different (desktop only)
        if (img.thumbnailUrl && img.thumbnailUrl !== img.url) {
          const thumbImg = new Image();
          thumbImg.src = img.thumbnailUrl;
        }
      });
    });

    // Race against timeout to avoid blocking too long
    const timeout = new Promise(resolve => setTimeout(resolve, timeoutMs));
    return Promise.race([Promise.all(imagePromises), timeout]);
  }

  /**
   * Update the page with a new product
   * Preloads images first, then uses requestAnimationFrame to batch DOM updates
   */
  async updateProduct(product) {
    // Update state first (no DOM changes)
    this.currentProduct = { handle: product.handle, id: product.id };
    this.selectedColour = product.colour.label;
    this.selectedHardware = product.hardware;

    // Push history state
    const newUrl = `/products/${product.handle}`;
    window.history.pushState({ productHandle: product.handle }, '', newUrl);

    // Add loading class to gallery/thumbnails for smooth transition
    const gallery = this.sectionId ? document.querySelector(`#Slider-Gallery-${this.sectionId}`) : null;
    const thumbnailContainer = this.sectionId ? document.querySelector(`#Slider-Thumbnails-${this.sectionId}`) : null;
    
    if (gallery) gallery.classList.add('images-loading');
    if (thumbnailContainer) thumbnailContainer.classList.add('images-loading');

    // Mobile: non-blocking (only 1 visible image, CSS transition handles fade-in)
    // Desktop: blocking preload to prevent flash when multiple images update
    const isMobile = window.innerWidth < 750;
    
    if (!isMobile) {
      await this.preloadImages(product, 200);
    }
    // Mobile: skip preload entirely - proceed immediately to DOM updates

    // Batch all visual updates in a single animation frame to minimize reflows
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        this.updateProductImages(product);
        // Modal update deferred - will update when modal is opened
        this.pendingModalProduct = product;
        this.updateProductTitle(product);
        this.updateProductPrice(product);
        this.updateProductForm(product);
        this.updateColourDescription(product);
        this.updateAccordionDescription(product);
        this.updateInventory(product);
        this.updateActiveSwatches(product.handle);
        this.updateHardwareButtons();

        // Remove loading class after DOM updates (slight delay for paint)
        requestAnimationFrame(() => {
          if (gallery) gallery.classList.remove('images-loading');
          if (thumbnailContainer) thumbnailContainer.classList.remove('images-loading');
        });

        resolve();
      });
    });
  }

  /**
   * Update product images in gallery
   * Optimized to reduce DOM queries by combining operations in single loop
   */
  updateProductImages(product) {
    if (!this.sectionId) return;

    const gallery = document.querySelector(`#Slider-Gallery-${this.sectionId}`);
    if (!gallery || !product.images || product.images.length === 0) return;

    const mediaItems = gallery.querySelectorAll('.product__media-item:not([data-mo-carousel])');

    // Responsive srcset widths matching main-product.liquid
    const srcsetWidths = [375, 550, 750, 990, 1200];
    
    // Single loop handles both image updates and active state
    mediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (product.images[index] && img) {
        const newImg = product.images[index];
        const oldSrc = img.src;

        // Set dimensions before src to reserve space and prevent CLS
        img.width = newImg.width || img.width;
        img.height = newImg.height || img.height;

        // Build responsive srcset from base URL by replacing width parameter
        const baseUrl = newImg.url;
        const srcset = srcsetWidths
          .map(w => baseUrl.replace(/width=\d+/, `width=${w}`) + ` ${w}w`)
          .join(', ');

        img.src = baseUrl;
        img.srcset = srcset;
        img.alt = newImg.alt;

        item.style.display = '';

        } else {
        // Hide surplus slots so old images don't bleed through
        item.style.display = 'none';
      }
      // Handle active state in same loop - avoids second querySelectorAll
      item.classList.toggle('is-active', index === 0);
    });

    gallery.scrollTo({ left: 0, behavior: 'auto' });
    
    // Skip thumbnail updates on mobile - thumbnails not visible
    if (window.innerWidth >= 750) {
      this.updateThumbnails(product.images);
    }
  }

  /**
   * Update thumbnail images
   * Optimized to use toggleAttribute and avoid nested querySelectorAll
   */
  updateThumbnails(images) {
    if (!this.sectionId) return;

    const thumbnailContainer = document.querySelector(`#Slider-Thumbnails-${this.sectionId}`);
    if (!thumbnailContainer) return;

    const thumbnails = thumbnailContainer.querySelectorAll('.thumbnail-list__item:not([data-mo-carousel])');

    thumbnails.forEach((thumbItem, index) => {
      const thumbImg = thumbItem.querySelector('img');
      const thumbButton = thumbItem.querySelector('.thumbnail');

      if (images[index] && thumbImg) {
        // Use thumbnailUrl if available, otherwise fall back to main url
        const thumbUrl = images[index].thumbnailUrl || images[index].url;
        
        // Must update both src AND srcset - browser prioritizes srcset
        thumbImg.src = thumbUrl;
        thumbImg.srcset = thumbUrl;
        thumbImg.alt = images[index].alt;

        thumbItem.style.display = '';

        // Use toggleAttribute to set aria-current only on first item
        if (thumbButton) {
          thumbButton.toggleAttribute('aria-current', index === 0);
        }
      } else {
        thumbItem.style.display = 'none';
      }
    });

    const thumbnailSlider = thumbnailContainer.querySelector('.thumbnail-list');
    if (thumbnailSlider) {
      thumbnailSlider.scrollTo({ left: 0, behavior: 'auto' });
    }
  }

  /**
   * Update product modal content for lightbox
   * Rebuilds modal HTML to match new product images
   */
  updateProductModal(product) {
    if (!this.sectionId) return;

    const modal = document.querySelector(`#ProductModal-${this.sectionId}`);
    if (!modal) return;

    const modalContent = modal.querySelector('[role="document"]');
    if (!modalContent || !product.images || product.images.length === 0) return;

    // Use smaller images on mobile (max 750w for ~375px viewport @ 2x)
    // Desktop uses larger images for full-screen lightbox viewing
    const isMobile = window.innerWidth < 750;
    const modalWidths = isMobile ? [375, 550, 750] : [550, 1100, 1445, 1680, 2048];
    const maxWidth = isMobile ? 750 : 2048;
    
    // Build new modal content HTML from product images
    let modalHTML = '';
    product.images.forEach((img, index) => {
      const isFirst = index === 0;
      // Use actual media ID if available, otherwise generate one
      const mediaId = img.id || `${product.id}-${index}`;
      const sectionMediaId = `${this.sectionId}-${mediaId}`;
      
      // Calculate display dimensions based on maxWidth to prevent oversized canvas
      const aspectRatio = img.width && img.height ? img.width / img.height : 1;
      const displayWidth = Math.min(img.width || maxWidth, maxWidth);
      const displayHeight = Math.round(displayWidth / aspectRatio);
      
      // Build responsive srcset for modal from base URL
      const baseUrl = img.url.replace(/width=\d+/, `width=${maxWidth}`);
      const srcset = modalWidths
        .map(w => img.url.replace(/width=\d+/, `width=${w}`) + ` ${w}w`)
        .join(', ');

      modalHTML += `
        <img
          class="global-media-settings global-media-settings--no-shadow${isFirst ? ' active' : ''}"
          src="${baseUrl}"
          srcset="${srcset}"
          sizes="${isMobile ? '100vw' : '(min-width: 750px) calc(100vw - 22rem), 1100px'}"
          alt="${img.alt || product.title}"
          loading="${isFirst ? 'eager' : 'lazy'}"
          width="${displayWidth}"
          height="${displayHeight}"
          data-media-id="${sectionMediaId}"
        >
      `;
    });

    modalContent.innerHTML = modalHTML;

    // Update gallery data-media-id attributes to match new IDs
    this.updateGalleryMediaIds(product);
  }

  /**
   * Update gallery media toggle buttons with new data-media-id values
   * Ensures gallery buttons match modal content after product switch
   */
  updateGalleryMediaIds(product) {
    if (!this.sectionId) return;

    const gallery = document.querySelector(`#Slider-Gallery-${this.sectionId}`);
    if (!gallery) return;

    const mediaToggles = gallery.querySelectorAll('.product__media-toggle');

    mediaToggles.forEach((toggle, index) => {
      if (product.images[index]) {
        // Use actual media ID if available, otherwise generate one
        const mediaId = product.images[index].id || `${product.id}-${index}`;
        const sectionMediaId = `${this.sectionId}-${mediaId}`;
        toggle.setAttribute('data-media-id', sectionMediaId);
      }
    });
  }

  /**
   * Update product title
   */
  updateProductTitle(product) {
    const titleElement = document.querySelector('.product__title h1, .product__title h2');
    if (titleElement) {
      titleElement.textContent = product.title.split(' | ')[0];
    }

    const descElement = document.querySelector('.product__description');
    if (descElement && product.description) {
      descElement.innerHTML = product.description;
    }
  }

  /**
   * Update product price
   */
  updateProductPrice(product) {
    const formatter = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: this.data.currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    const priceContainer = document.querySelector('.price');
    const price = product.price / 100;
    const comparePrice = product.compareAtPrice ? product.compareAtPrice / 100 : null;

    if (comparePrice && comparePrice > price) {
      const compareElement = document.querySelector('.price__sale .price-item--regular');
      if (compareElement) compareElement.textContent = formatter.format(comparePrice);

      const saleElement = document.querySelector('.price__sale .price-item--sale');
      if (saleElement) saleElement.textContent = formatter.format(price);

      if (priceContainer) priceContainer.classList.add('price--on-sale');

      const badge = document.querySelector('.price .badge.price__badge-sale');
      if (badge) {
        const badgeTag = product.tags?.find(t => t.startsWith('badge_'));
        badge.textContent = badgeTag ? badgeTag.replace('badge_', '') : 'Sale';
      }
    } else {
      const regularElement = document.querySelector('.price__regular .price-item--regular');
      if (regularElement) regularElement.textContent = formatter.format(price);

      if (priceContainer) priceContainer.classList.remove('price--on-sale');
    }

    if (priceContainer) {
      if (!product.available) {
        priceContainer.classList.add('price--sold-out');
      } else {
        priceContainer.classList.remove('price--sold-out');
      }
    }

    this.updateButtonState(product);
  }

  /**
   * Update form variant ID
   */
  updateProductForm(product) {
    const productForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    if (!productForm) {
      return;
    }

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = product.variantId;
      variantInput.disabled = false;
      // Note: No change event dispatch needed - form reads value at submit time
      // and updateButtonState() already handles button text updates
    }

    const quantityInput = productForm.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = quantityInput.getAttribute('min') || '1';
    }

    const installmentForm = document.querySelector('form.installment');
    if (installmentForm) {
      const installmentInput = installmentForm.querySelector('input[name="id"]');
      if (installmentInput) {
        installmentInput.value = product.variantId;
        installmentInput.disabled = false;
      }
    }

    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      productInfo.dataset.productId = product.id;
    }
  }

  /**
   * Update colour description text
   */
  updateColourDescription(product) {
    if (this.elements.descriptionText) {
      this.elements.descriptionText.textContent = product.colour.label;
    }
  }

  /**
   * Update accordion description text based on product toggle
   */
  updateAccordionDescription(product) {
    const accordionText = document.querySelector('.text-with-accordion-content .rte');
    if (!accordionText) return;

    // Check toggle: use product description if true AND description exists, otherwise use metaobject
    let descriptionText = product.metaobjectDescription || '';
    if (product.useProductDescription === true && product.description) {
      descriptionText = product.description;
    }

    if (descriptionText) {
      accordionText.innerHTML = descriptionText;
    }
  }

  /**
   * Update inventory display
   */
  updateInventory(product) {
    const inventoryContainer = this.container.querySelector('.product__inventory--hardware');
    if (!inventoryContainer) return;

    const qty = product.quantityAvailable || 0;
    const svgWrapper = inventoryContainer.querySelector('.svg-wrapper');

    let textNode = null;
    for (let node of inventoryContainer.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        textNode = node;
        break;
      }
    }

    if (qty > 5) {
      if (svgWrapper) svgWrapper.style.color = 'rgb(62, 214, 96)';
      if (textNode) textNode.textContent = 'In stock';
    } else if (qty > 0) {
      if (svgWrapper) svgWrapper.style.color = 'rgb(238, 148, 65)';
      if (textNode) textNode.textContent = `Low stock: ${qty} left`;
    } else {
      if (svgWrapper) svgWrapper.style.color = 'rgb(200, 200, 200)';
      if (textNode) textNode.textContent = 'Out of stock';
    }
  }

  /**
   * Update active swatch styling
   * Optimized to query only current active instead of all swatches
   */
  updateActiveSwatches(handle) {
    // Remove active from current active swatch only (not all swatches)
    const currentActive = this.elements.swatchGrid.querySelector('.colour-swatch.active');
    if (currentActive) currentActive.classList.remove('active');

    // Add active to new swatch
    const newActive = this.elements.swatchGrid.querySelector(`[data-product-handle="${handle}"] .colour-swatch`);
    if (newActive) newActive.classList.add('active');
  }

  /**
   * Update hardware button states
   * Optimized to use hardwareLookup Map for O(1) availability checks
   */
  updateHardwareButtons() {
    this.elements.hardwareContainer.querySelectorAll('.hardware-filter-button').forEach(button => {
      const hw = button.dataset.hardware;
      const isActive = hw === this.selectedHardware;

      // Use pre-built lookup map for O(1) check instead of O(n) array search
      const key = `${this.selectedColour}|${hw}`;
      const exists = this.hardwareLookup.has(key);

      // Use classList.toggle for cleaner state management
      button.classList.toggle('active', isActive);
      button.classList.toggle('inactive', !isActive && exists);
      button.classList.toggle('not-available', !exists);
    });
  }

  /**
   * Update colour swatch handles after hardware change
   */
  updateColourSwatchHandles() {
    this.elements.swatchGrid.querySelectorAll('.colour-swatch-wrapper').forEach(wrapper => {
      const colourLabel = wrapper.dataset.colourLabel;

      const targetProduct = this.filteredSiblings.find(p =>
        p.colour.label === colourLabel && p.hardware === this.selectedHardware
      );

      const button = wrapper.querySelector('.colour-swatch');

      if (targetProduct) {
        wrapper.dataset.productHandle = targetProduct.handle;
        wrapper.classList.remove('not-available');
        if (button) button.classList.remove('not-available');
      } else {
        wrapper.classList.add('not-available');
        if (button) button.classList.add('not-available');
      }
    });
  }

  /**
   * Update button state
   */
  updateButtonState(product) {
    const submitButton = document.querySelector('.product-form__submit');
    if (!submitButton) return;

    const buttonSpan = submitButton.querySelector('span');

    if (!product) {
      submitButton.setAttribute('aria-disabled', 'true');
      if (buttonSpan) buttonSpan.textContent = 'Unavailable';
    } else if (product.available) {
      submitButton.removeAttribute('aria-disabled');
      if (buttonSpan) buttonSpan.textContent = 'Add to basket';
    } else {
      submitButton.setAttribute('aria-disabled', 'true');
      if (buttonSpan) buttonSpan.textContent = 'Sold out';
    }
  }

  /**
   * Initialize browser history handling
   */
  initBrowserHistory() {
    window.addEventListener('popstate', (event) => {
      if (event.state?.productHandle) {
        const product = this.data.siblings.find(s => s.handle === event.state.productHandle);
        if (product) {
          this.updateProduct(product);
        }
      } else {
        window.location.reload();
      }
    });

    document.addEventListener('click', (e) => {
      const target = e.target.closest('a[href*="/products/"]');
      if (target && this.container.contains(target)) {
        const href = target.getAttribute('href');
        const handle = href.split('/').pop();

        const isSibling = this.data.siblings.some(p => p.handle === handle);
        if (isSibling) {
          e.preventDefault();
          const product = this.data.siblings.find(p => p.handle === handle);
          if (product) {
            this.updateProduct(product);
          }
        }
      }
    }, true);
  }
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const loader = new ProductSiblingsLoader();
      loader.init();
    });
  } else {
    const loader = new ProductSiblingsLoader();
    loader.init();
  }

})();
